import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Pagination } from '@/components/ui/Pagination'
import { Card } from '@/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import {
  useAdminGetCustomers,
  useAdminUpdateMallInfo,
  getAdminGetCustomersQueryKey,
} from '@/api/generated/v2-admin/v2-admin'
import type {
  CustomerListItemResponse,
  MallInfoUpdateRequest,
  PaginatedCustomerListResponse as PaginatedCustomerListResponse,
  ShopSummaryResponse,
  TotalValueBoardStatus,
} from '@/api/schemas'
import { adminApi } from '@/lib/adminApi'
import { formatPhoneDisplay, formatPhoneInput, stripPhone } from '@/lib/phoneUtils'
import { formatDateKST } from '@/lib/utils'
import {
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ExternalLink,
  Pencil,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

export const Route = createFileRoute('/admin/customers')({
  component: AdminCustomers,
})

function AdminCustomers() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // 검색/필터/정렬/페이지 상태
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  // cohort는 강의명(자유 문자열) — 부분 일치 텍스트 검색
  const [cohortFilter, setCohortFilter] = useState<string>('')
  const [includeInactive, setIncludeInactive] = useState(false)
  // 캠퍼스 미연동 몰만 보기 (REQ-157). 면제(화이트리스트) 몰은 미연동에 포함되지 않는다.
  const [unlinkedOnly, setUnlinkedOnly] = useState(false)
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const perPage = 20

  // Lexicon 택소노미 전체 싱크 상태
  const [syncingLexicon, setSyncingLexicon] = useState(false)

  // 택소노미 정의를 갱신했을 때 전 고객사 Mixpanel Lexicon 을 강제 재싱크한다.
  const handleSyncLexicon = async () => {
    if (
      !window.confirm(
        '택소노미 정의를 SA 연동된 모든 고객사 Mixpanel Lexicon 에 다시 올릴까요?',
      )
    )
      return

    setSyncingLexicon(true)
    try {
      const result = await adminApi.syncLexicon()
      toast.success(
        `Lexicon 싱크 완료 — 대상 ${result.total} · 갱신 ${result.synced} · 건너뜀 ${result.skipped} · 실패 ${result.failed}`,
      )
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Lexicon 싱크에 실패했어요')
    } finally {
      setSyncingLexicon(false)
    }
  }

  // 편집 모달 상태
  const [editingCustomer, setEditingCustomer] =
    useState<CustomerListItemResponse | null>(null)
  const [editForm, setEditForm] = useState<MallInfoUpdateRequest>({})

  // 검색 디바운스 (800ms) — 여러 글자 입력 중 중간에 검색되지 않도록 충분히 여유
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(1) // 검색 시 1페이지로
    }, 800)
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [searchTerm])

  // 목록 페이지가 아닌 경우 query를 비활성화
  const isListPage = location.pathname === '/admin/customers'

  // Orval Query 훅: 고객사 목록 조회
  const queryParams = {
    page,
    per_page: perPage,
    search: debouncedSearch || undefined,
    cohort: cohortFilter || undefined,
    include_inactive: includeInactive || undefined,
    campus_status: unlinkedOnly ? ('unlinked' as const) : undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  }
  const { data: customersRaw, isLoading, error } = useAdminGetCustomers(
    queryParams,
    { query: { enabled: isListPage } },
  )
  const data = (customersRaw ?? null) as PaginatedCustomerListResponse | null

  // Orval Mutation 훅: Mall 정보 수정
  const updateMallMutation = useAdminUpdateMallInfo({
    mutation: {
      onSuccess: () => {
        setEditingCustomer(null)
        // 목록 새로고침
        queryClient.invalidateQueries({
          queryKey: getAdminGetCustomersQueryKey(queryParams),
        })
        toast.success('고객사 정보가 저장되었어요.')
      },
      onError: () => {
        toast.error('저장에 실패했어요.')
      },
    },
  })

  // 정렬 토글 핸들러
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
    setPage(1)
  }

  // 정렬 아이콘 컴포넌트
  const SortIcon = ({ field }: { field: string }) => {
    if (sortBy !== field)
      return <ChevronsUpDown className="h-4 w-4 text-outline-variant" />
    return sortOrder === 'asc' ? (
      <ChevronUp className="h-4 w-4 text-primary" />
    ) : (
      <ChevronDown className="h-4 w-4 text-primary" />
    )
  }

  // 편집 모달 열기
  const openEditModal = (customer: CustomerListItemResponse) => {
    setEditingCustomer(customer)
    setEditForm({
      owner_name: customer.owner_name ?? '',
      owner_phone: customer.owner_phone ? formatPhoneInput(customer.owner_phone) : '',
      cohort: customer.cohort,
      max_shops: customer.max_shops ?? 1,
    })
  }

  // 편집 저장
  const handleSave = () => {
    if (!editingCustomer) return
    updateMallMutation.mutate({
      mallId: editingCustomer.id,
      data: {
        ...editForm,
        owner_phone: editForm.owner_phone ? stripPhone(editForm.owner_phone) : editForm.owner_phone,
      },
    })
  }

  // URL이 상세 페이지인 경우 Outlet 렌더링
  if (!isListPage) {
    return <Outlet />
  }

  if (isLoading && !data) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-surface-high rounded w-64 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-surface-high h-12 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="text-error text-lg">
            고객사 목록을 불러오는데 실패했어요.
          </div>
        </div>
      </div>
    )
  }

  const customers = data?.items ?? []
  const totalPages = data?.total_pages ?? 1
  const total = data?.total ?? 0

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* 헤더 */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl font-bold text-on-surface">고객사 관리</h1>
          <p className="mt-1 text-sm text-on-surface-variant">총 {total}개 고객사</p>
        </div>
        {/* 택소노미 정의 갱신 후 전 고객사 Lexicon 재싱크 (평소엔 연동 시 자동) */}
        <Button
          variant="secondary"
          onClick={handleSyncLexicon}
          disabled={syncingLexicon}
          className="min-h-[44px] sm:min-h-0"
        >
          {syncingLexicon ? '싱크 중…' : 'Lexicon 전체 싱크'}
        </Button>
      </div>

      {/* 검색 + 필터 */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1 max-w-md">
          <Input
            type="text"
            placeholder="카페24 ID / 샵이름 / 대표자 / 전화번호로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="relative">
          <Input
            type="text"
            value={cohortFilter}
            onChange={(e) => {
              setCohortFilter(e.target.value)
              setPage(1)
            }}
            placeholder="강의/기수 필터"
            className="w-40"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-on-surface-variant cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => {
              setIncludeInactive(e.target.checked)
              setPage(1)
            }}
            className="rounded border-border text-primary focus:ring-ring"
          />
          삭제된 몰 포함
        </label>
        <label className="flex items-center gap-2 text-sm text-on-surface-variant cursor-pointer select-none">
          <input
            type="checkbox"
            checked={unlinkedOnly}
            onChange={(e) => {
              setUnlinkedOnly(e.target.checked)
              setPage(1)
            }}
            className="rounded border-border text-primary focus:ring-ring"
          />
          캠퍼스 미연동만
        </label>
      </div>

      {/* 테이블 */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="text-sm">
            <TableHeader>
              <TableRow className="bg-surface">
                <TableHead
                  className="cursor-pointer hover:bg-surface-low select-none"
                  onClick={() => handleSort('cafe24_mall_id')}
                >
                  <div className="flex items-center gap-1">
                    카페24 ID
                    <SortIcon field="cafe24_mall_id" />
                  </div>
                </TableHead>
                <TableHead>샵</TableHead>
                <TableHead>퍼널 보드</TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-surface-low select-none"
                  onClick={() => handleSort('owner_name')}
                >
                  <div className="flex items-center gap-1">
                    대표자
                    <SortIcon field="owner_name" />
                  </div>
                </TableHead>
                <TableHead>전화번호</TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-surface-low select-none"
                  onClick={() => handleSort('cohort')}
                >
                  <div className="flex items-center gap-1">
                    기수
                    <SortIcon field="cohort" />
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-surface-low select-none"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    가입일
                    <SortIcon field="created_at" />
                  </div>
                </TableHead>
                <TableHead className="text-center w-16">편집</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={8} className="px-4 py-12 text-center">
                    <p className="text-on-surface-variant">
                      {debouncedSearch || cohortFilter || unlinkedOnly
                        ? '검색 결과가 없어요.'
                        : '등록된 고객사가 없어요.'}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                customers.flatMap((customer) => {
                  // Shop이 없으면 빈 행 1개, 있으면 Shop 수만큼 행 생성
                  const shops =
                    customer.shops && customer.shops.length > 0
                      ? [...customer.shops].sort((a, b) => a.shop_no - b.shop_no)
                      : ([null] as (ShopSummaryResponse | null)[])
                  const rowCount = shops.length

                  return shops.map((shop, shopIdx) => (
                    <TableRow
                      key={shop?.id || customer.id}
                      className={`
                        ${shopIdx === rowCount - 1 ? 'border-b' : 'border-b-0'}
                        last:border-b-0 hover:bg-transparent
                        ${!(customer.is_active ?? true) ? 'opacity-50' : ''}
                      `}
                    >
                      {/* 몰ID: 첫 번째 Shop 행에만 rowSpan */}
                      {shopIdx === 0 && (
                        <TableCell
                          className="font-medium text-on-surface"
                          rowSpan={rowCount}
                        >
                          <div className="flex items-center gap-2">
                            {customer.cafe24_mall_id}
                            {!(customer.is_active ?? true) && (
                              <Badge variant="destructive" className="text-xs whitespace-nowrap">
                                삭제됨
                                {customer.uninstalled_at && (
                                  <span className="ml-1">
                                    ({formatDateKST(customer.uninstalled_at).split(' ')[0]})
                                  </span>
                                )}
                              </Badge>
                            )}
                            {/* 캠퍼스 연동 없이 앱만 설치된 활성 몰 (면제·삭제된 몰은 백엔드가 unlinked 로 주지 않는다) */}
                            {customer.campus_status === 'unlinked' && (
                              <Badge variant="warning" className="text-xs whitespace-nowrap">
                                캠퍼스 미연동
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      )}

                      {/* 샵: 각 행마다 표시, hover는 이 셀에만 적용 */}
                      <TableCell
                        className={`py-2.5 ${shop ? 'cursor-pointer hover:bg-primary/5 transition-colors rounded' : ''}`}
                        onClick={
                          shop
                            ? () =>
                                navigate({
                                  to: '/admin/customers/$customerId',
                                  params: { customerId: shop.id },
                                })
                            : undefined
                        }
                      >
                        {shop ? (
                          <span className="text-sm text-primary font-medium">
                            {shop.shop_name || `샵 #${shop.shop_no}`}
                          </span>
                        ) : (
                          <span className="text-outline-variant text-sm">
                            샵 없음
                          </span>
                        )}
                      </TableCell>

                      {/* 퍼널 보드: 총가치 대시보드(v2.0) 진입 상태 (샵 단위) */}
                      <TableCell className="py-2.5">
                        {!shop ? (
                          <span className="text-outline-variant text-sm">-</span>
                        ) : (
                          <FunnelBoardCell shop={shop} />
                        )}
                      </TableCell>

                      {/* 대표자, 전화번호, 기수, 가입일, 편집: 첫 번째 행에만 rowSpan */}
                      {shopIdx === 0 && (
                        <>
                          <TableCell
                            className="text-on-surface"
                            rowSpan={rowCount}
                          >
                            {customer.owner_name || (
                              <span className="text-outline-variant">-</span>
                            )}
                          </TableCell>
                          <TableCell
                            className="text-on-surface"
                            rowSpan={rowCount}
                          >
                            {customer.owner_phone ? formatPhoneDisplay(customer.owner_phone) : (
                              <span className="text-outline-variant">-</span>
                            )}
                          </TableCell>
                          <TableCell rowSpan={rowCount}>
                            {customer.cohort ? (
                              <Badge variant="secondary">
                                {customer.cohort}
                              </Badge>
                            ) : (
                              <span className="text-outline-variant">-</span>
                            )}
                          </TableCell>
                          <TableCell
                            className="text-on-surface-variant text-xs"
                            rowSpan={rowCount}
                          >
                            {formatDateKST(customer.created_at).split(' ').slice(0, 2).join(' ')}
                          </TableCell>
                          <TableCell
                            className="text-center"
                            rowSpan={rowCount}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                openEditModal(customer)
                              }}
                              className="p-1.5 text-outline-variant hover:text-primary hover:bg-primary/10 rounded transition-colors"
                              title="편집"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* 페이지네이션 */}
      {total > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-on-surface-variant">
            총 {total}개 중 {(page - 1) * perPage + 1}-
            {Math.min(page * perPage, total)}
          </p>
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* 편집 모달 */}
      <Dialog
        open={!!editingCustomer}
        onOpenChange={(open) => !open && setEditingCustomer(null)}
        size="md"
      >
        <DialogContent>
          <DialogHeader onClose={() => setEditingCustomer(null)}>
            <DialogTitle>
              고객사 정보 편집 - {editingCustomer?.cafe24_mall_id}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                대표자명
              </label>
              <Input
                value={editForm.owner_name ?? ''}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    owner_name: e.target.value,
                  }))
                }
                placeholder="대표자명 입력"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                전화번호
              </label>
              <Input
                type="tel"
                value={editForm.owner_phone ?? ''}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    owner_phone: formatPhoneInput(e.target.value, prev.owner_phone ?? ''),
                  }))
                }
                placeholder="010-1234-5678"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                강의 / 기수
              </label>
              <Input
                type="text"
                value={editForm.cohort ?? ''}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    cohort: e.target.value || null,
                  }))
                }
                placeholder="강의명 또는 기수 (예: 1, 2025 가을 1기)"
              />
            </div>
            {/* 최대 샵 수 */}
            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                최대 샵 수
              </label>
              <Input
                type="number"
                min={1}
                value={editForm.max_shops ?? 1}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    max_shops: e.target.value ? Number(e.target.value) : 1,
                  }))
                }
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setEditingCustomer(null)}
              disabled={updateMallMutation.isPending}
            >
              취소
            </Button>
            <Button onClick={handleSave} disabled={updateMallMutation.isPending}>
              {updateMallMutation.isPending ? '저장 중...' : '저장'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** 총가치 보드가 없는 상태 → 목록 배지 (문구·판정은 설정 화면 TotalValueBoardCard 와 같은 축) */
const BOARD_STATUS_BADGE: Record<
  Exclude<TotalValueBoardStatus['status'], 'ready'>,
  { label: string; variant: 'secondary' | 'warning' | 'danger' | 'info' }
> = {
  pending_first_check: { label: '확인 전', variant: 'secondary' },
  insufficient_data: { label: '표본 부족', variant: 'info' },
  deferred: { label: '생성 대기', variant: 'info' },
  plan_blocked: { label: '플랜 미지원', variant: 'warning' },
  sa_permission: { label: 'SA 권한 없음', variant: 'warning' },
  sa_missing: { label: 'SA 미입력', variant: 'warning' },
  failed: { label: '확인 실패', variant: 'danger' },
  unknown: { label: '확인 실패', variant: 'danger' },
}

/**
 * 어드민 목록 '퍼널 보드' 셀 (REQ-158).
 *
 * 예전엔 v1.0 손 복제 보드 ID(`mixpanel_funnel_board_id`)만 봐서, 잡이 v2.0 보드를
 * 만든 샵도 '미설정' 으로 보였다. 이제 판정은 백엔드 `build_total_value_board_status`
 * 한 곳이 하고(`total_value_board`), 여기선 그대로 그린다:
 *   - ready → 보드 링크
 *   - 그 외 → 보드가 없는 이유 배지 (hover 에 상세 사유)
 *   - null → Mixpanel 미연동
 * v2 가 없고 v1 만 있으면 'v1' 표식을 덧붙인다 — 캠퍼스 일부 진입점이 아직 v1 을 쓴다.
 */
function FunnelBoardCell({ shop }: { shop: ShopSummaryResponse }) {
  const board = shop.total_value_board
  const hasV1 = !!shop.mixpanel_funnel_board_id

  if (!board) {
    return <Badge variant="secondary">미연동</Badge>
  }

  if (board.status === 'ready' && board.board_url) {
    return (
      <a
        href={board.board_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline whitespace-nowrap"
      >
        2.0 보드
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
    )
  }

  // ready 인데 URL 이 비는 모순은 unknown 과 같이 '확인 실패' 로 드러낸다
  const badge =
    board.status === 'ready' ? BOARD_STATUS_BADGE.unknown : BOARD_STATUS_BADGE[board.status]
  // 표본 부족은 진행도를 붙인다. 수치가 없으면 0 으로 채우지 않고 생략한다.
  const progress =
    board.status === 'insufficient_data' && board.current_views != null && board.min_views != null
      ? ` ${board.current_views.toLocaleString()}/${board.min_views.toLocaleString()}`
      : ''

  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap" title={board.reason ?? undefined}>
      <Badge variant={badge.variant}>
        {badge.label}
        {progress}
      </Badge>
      {hasV1 && <span className="text-xs text-on-surface-variant">v1</span>}
    </div>
  )
}
